import type React from 'react';

/**
 * One feedback state's condition, AS DATA — the list a block builds once and every surface draws.
 *
 * This type used to live in `components/task/condition-chips.tsx`, beside the two renderers, and
 * that placement was the reason the codebase ended up with two competing "master" derivations
 * instead of one. `lib/` cannot import from `components/`, so anything Flow needs has to live here;
 * `stateChipLabel` therefore returned a plain `string`, and its own note recorded the consequence:
 *
 *   > The shared module could not hold the answer before now because `stateChipLabel` returns a
 *   > `string`, and a string cannot carry "option A AND option B" or the media icon an MCQ option
 *   > puts in front of its text.
 *
 * So the chip surfaces built a richer type they could not share with Flow, Flow kept a poorer one
 * it could not share with them, and each block picked a side. Moving the TYPE down here dissolves
 * that: `React.ReactNode` is a type-only import with no runtime dependency on any component, so
 * `lib/` can describe a chip list without being able to render one. The renderers —
 * `ConditionChipRow` for the panel, `toTestBarChips` for the legend — stay in `components/`, which
 * is where they belong and where they always were.
 *
 * What is deliberately NOT here: `id`, `active`, `category`, `track`. Those are the LEGEND's view
 * state — which chip fired, which row it belongs in — and no other surface has a notion of them.
 * They are added by `toTestBarChips`, which is the whole difference between the two surfaces.
 */
/**
 * ═══ THE CONTRACT, and why it exists ═══════════════════════════════════════════════════════════
 *
 * Read this before adding a chip surface, a mode, or a per-block special case. The four
 * value-range blocks (numerical, slider, compass, angle) once answered "what does this state's
 * condition look like" in about twenty places, and the shape of that mess is worth remembering
 * because every step of it looked reasonable at the time.
 *
 * ── The rule ──────────────────────────────────────────────────────────────────────────────────
 *
 * ONE derivation per block, taking the roll as a PARAMETER:
 *
 *     <block>Chips(data, state, roll: ChipRoll, t, locale, targetedTriggers) => ConditionChips
 *
 * Every surface RENDERS that list and derives nothing: the feedback panel and the collapsed card
 * pass `null`, the panel under a scenario passes the scenario, the test legend passes the live
 * roll, and the editor's answer bar passes whatever its panel has. The two readings cannot drift,
 * because they are one code path with one argument different.
 *
 * ── How it went wrong the first time ──────────────────────────────────────────────────────────
 *
 * The derivations were partitioned by MODE (unrolled/rolled) and by SURFACE (panel, legend, answer
 * bar, card), and each block partitioned differently: compass had eight, angle seven, slider four,
 * numerical five. Within a block the two SURFACES always agreed — the legend simply always has a
 * roll while the panel is unrolled at rest — so a MODE difference presented as a SURFACE
 * difference, and every fix aimed at one surface left the other three behind. That is the trap: if
 * you find yourself fixing "the test bar", check whether you are actually fixing "the rolled
 * reading".
 *
 * ── Three specific mistakes not to repeat ─────────────────────────────────────────────────────
 *
 * 1. DO NOT add a node-level helper for the answer bar. The bar and the panel's formula chip wrap
 *    their content in a SINGLE chip span, so per-answer nodes render as one chip with the
 *    connector inside it, which is unreadable and — on a live surface — wrong, because one chip
 *    lights up for two different conditions. The unit is a chip LIST; use `ConditionChipRow`.
 *
 * 2. MAP the answer list, never FILTER it. Every "which formulas are here" helper filters to
 *    formula-bearing entries; that is right for asking "does this block vary?" and wrong for
 *    asking "what are its answers?". Filtering is what silently dropped a literal sitting beside a
 *    variable, and dropped the second bound of a `between` row with one equation end.
 *    `answerExprTexts` maps; `formulaAnswerTexts` filters.
 *
 * 3. ONE CHIP PER CONDITION A VALUE CAN LAND IN. Disjoint gaps get separate chips (`< 26.88` and
 *    `> 29.12`), because a teacher moving a slider needs to see WHICH side fired; a single chip
 *    reading "< 26.88 or > 29.12" lights for both and tells them nothing. A BOUNDED gap keeps its
 *    two bounds together ("> 76 and < 98") — that is one interval, so one chip is one condition.
 *
 * ── The display rules, settled with the user ──────────────────────────────────────────────────
 *
 *   unrolled  one chip per AUTHORED answer, literals included; the complement reads `not {chip}`
 *   rolled    exactly what a hard-coded answer would produce — resolved bands, and per-GAP chips
 *             for the complement (no collapsing several gaps into one summary chip)
 *
 * CONNECTORS differ by state AND by mode, and that is De Morgan rather than drift:
 *
 *   Correct                     answers are alternatives                  -> "or"
 *   Default Incorrect, unrolled not(x=A or x=B) == x!=A and x!=B          -> "and"
 *   Default Incorrect, rolled   the same region as disjoint gaps          -> "or"
 *   slider across handles       conjunctive, so Correct "and", complement "or" (it NESTS, via
 *                               `joinChipGroups`, which FITB also uses for its blanks)
 *
 * The ANSWER BAR states what a setup RESOLVES TO wherever it can: a literal `100 ± 2` prints
 * "98 to 102", because the teacher already knows what they typed and came to see what it covers.
 * Only an unresolved variable keeps the `± tol`, there being no band to print yet.
 *
 * See also `feedback_no_bare_braces_ever` (a `{name}` is a chip or an evaluated value, never
 * literal text — which is what the `formulas` field below exists to make possible from `lib/`).
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export type ConditionChip =
  | {
      kind: 'chip';
      label: React.ReactNode;
      evaluation: ChipEvaluation;
      slashed?: boolean;
      /**
       * Which authored thing this chip stands for — an option id, a blank id, a sentinel for a
       * catch-all.
       *
       * Only the LEGEND uses it, and only to decide which chip lights up. A fan-out over several
       * alternatives (Keyword's synonyms, MCQ's options, FITB's per-blank words) fires on exactly
       * one of them, and the legend has always highlighted just that one — but `active` on the
       * envelope is a property of the STATE, so a straight port would have lit every chip in the
       * fan-out at once. Carrying the identity on the chip is what lets one derivation serve both
       * the panel (which has no notion of firing) and the legend (which does).
       *
       * A chip without one falls back to the envelope's `active`, so blocks whose states don't fan
       * out are unaffected.
       */
      sourceId?: string;
      /**
       * Equations that must be drawn as variable CHIPS, appended after `label`.
       *
       * The escape hatch that lets a `lib/` derivation say "not {n}*2" without being able to render
       * a chip. `lib/` cannot import `FormulaWithValues` (it is a component), so before this a
       * lib-side derivation had exactly two options for a formula answer: interpolate the raw
       * `{n}` into a translated string, which is the bare-braces violation compass and angle
       * shipped ("Anything but {n}"), or resolve it with `num()`, which substitutes 0 and prints a
       * confident wrong number — which is what slider's Default Incorrect did ("0 to 0").
       *
       * Both renderers draw these through the app's one chip renderer, so the derivation stays
       * data and the chip stays a chip. `text` carries the flattened form for surfaces that cannot
       * draw nodes.
       */
      formulas?: string[];
      /**
       * This chip is NOT a term in the condition — it is shown so the reader can see the option
       * exists and constrains nothing.
       *
       * MCQ's multi-select answer key is a conjunction: every correct option present AND every
       * incorrect one absent. A neutral option is optional either way, so joining it with "and"
       * would claim a requirement that does not exist, and omitting it would hide an option the
       * student can freely tick. It trails the expression instead, drawn back.
       *
       * Semantic rather than styling: both renderers decide their own dimming from it, which is
       * what stops the two from drifting the way they already had — the legend drew this chip at
       * full strength with an italic tag, the panel drew it dimmed with a plain one.
       */
      muted?: boolean;
      /** A trailing parenthetical qualifying the chip — "(optional)". Supplied by the derivation
       *  because only it knows the wording; drawn by each renderer, which knows the styling. */
      tag?: string;
      /**
       * The same content as plain text, for a surface that cannot draw nodes.
       *
       * Needed because `label` is a `ReactNode` and most of them are elements: numerical's gap chips
       * wrap their bounds in spans, MCQ's option chips put a media icon in front of the text. A
       * flattening surface — Flow, or a collapsed answer bar — gets nothing usable from those, and
       * `[object Object]` on a Flow card is worse than a chip the card leaves out. So a block whose
       * labels are elements supplies this alongside; a block whose labels are strings does not need
       * to, since the string flattens itself.
       */
      text?: string;
    }
  /** Reads "or"/"and" between two chips — a fan-out is one condition written across several boxes,
   *  and without the word between them the boxes read as unrelated alternatives. */
  | { kind: 'connector'; word: 'and' | 'or' }
  /**
   * The state cannot fire. No surface writes that sentence itself — they pass the wording in, so
   * "Unreachable" is spelled once per surface rather than once per block.
   *
   * `label` overrides that default for a fan-out where only ONE term is dead: FITB's Correct is a
   * conjunction across blanks, so a passage with an answer for blank 1 and none for blank 2 needs
   * to say "Blank 2: Unreachable" in position. A bare "Unreachable" there would read as though the
   * whole state were dead, and dropping the term entirely (which the loop used to do) made an
   * unsatisfiable condition look satisfiable.
   */
  | { kind: 'unreachable'; label?: string };

export type ConditionChips = ConditionChip[];

/**
 * A chip's tone. Duplicated from `components/task/eval-chip.tsx` as a pure union rather than
 * imported, because a `lib` module must not depend on a component file even for a type — the import
 * graph is the thing being fixed here, and a type-only edge is still an edge that the next person
 * will follow with a value import.
 */
export type ChipEvaluation = 'correct' | 'incorrect' | 'neutral';

/**
 * Where a block resolves its variables from, and therefore how they read.
 *
 * `null` means "no roll": variables stay as chips (`{n}`), which is the feedback panel at rest.
 * A roll means resolved — and RESOLVED, not substituted: a bound of `{n}*10` reads `20`, not
 * `2*10`, because the accepted-answer chip beside it has always shown the evaluated number, and two
 * spellings of one value in a single row is the drift this type exists to end. The test bar always
 * passes its roll; the feedback panel passes its own scenario, which is null until the teacher
 * presses Minimum/Maximum/Random.
 */
export type ChipRoll = Record<string, string> | null;

/**
 * GROUPS of chips joined by one word — for a nested condition, where the connector inside a group
 * differs from the connector between them.
 *
 * Both blocks that fan out over two levels need this and both had it inline: FITB's Correct is
 * "(blank 1's synonyms, or'd) and (blank 2's, or'd)", and slider's is "(handle A's answers, or'd)
 * and (handle B's, or'd)" — with Default Incorrect flipping both connectors by De Morgan. A flat
 * `joinChips` cannot express either, because it puts the same word everywhere.
 */
export function joinChipGroups(groups: ConditionChips[], word: 'and' | 'or'): ConditionChips {
  return groups.flatMap((g, i) => i === 0 ? g : [{ kind: 'connector' as const, word }, ...g]);
}

/**
 * The comparison symbol each one-sided condition is written with.
 *
 * Not translated, and not an i18n key: `<` and `≥` are the same marks in every locale this app
 * ships, and routing them through a string table would be four keys per block that can only ever
 * drift apart. Numerical, slider and angle each had their own set, and all three said the same
 * thing.
 */
const CUT_SYMBOLS: Record<string, string> = { lt: '<', lte: '\u2264', gt: '>', gte: '\u2265' };

/** Whether this condition is a one-sided cut rather than a point or a span. */
export function isCutCondition(conditionType: string): boolean {
  return conditionType in CUT_SYMBOLS;
}

/** The symbol alone, for a caller building its own label — angle's variable-answer path states
 *  "> {x}" with the equation as a chip, so it cannot go through `cutChip`'s formatted value. */
export function cutSymbol(conditionType: string): string {
  return CUT_SYMBOLS[conditionType] ?? '';
}

/**
 * The accepted-answer chip for a one-sided condition — "> 25", "≤ 5.5 m", "≥ 25°".
 *
 * ## Why this is shared when the derivations around it are not
 *
 * Each block owns its own chip derivation on purpose, and an invariant enforces one per block.
 * This is the one RULE inside them that three blocks each wrote separately and each got
 * differently wrong:
 *
 *   - **Slider** filtered its bands on both bounds being finite. A cut is `{lo: 25, hi: Infinity}`,
 *     so the band was dropped and the feedback card showed NO correct answer at all.
 *   - **Angle** clamped the cut to its `[0,360]` domain, so "> 25" read "25° to 360°" — which
 *     says 25 is accepted when `gt` excludes it, and reads identically to "≥ 25".
 *   - **Numerical** got it right, and is here so that stays true rather than by luck.
 *
 * Two rules it encodes, both learned from those bugs:
 *
 * **Never clamp to a domain.** A span implies its endpoints are included; a cut's threshold is
 * exactly what may not be. The comparison is what the teacher authored, so the comparison is what
 * is shown.
 *
 * **Read the AUTHORED threshold, not the resolved number.** `num('')` is 0, a perfectly finite
 * number, so an untouched threshold field would otherwise advertise "> 0" as the correct answer —
 * the same trap the blank-answer filters elsewhere exist to avoid.
 *
 * `formatValue` is the block's own: it carries the unit, the degree sign, the locale and the
 * significant figures, none of which belong here.
 */
export function cutChip(
  conditionType: string,
  authoredThreshold: string,
  resolved: number,
  formatValue: (v: number) => string,
  evaluation: ChipEvaluation = 'correct',
  scope = '',
): ConditionChips | null {
  const symbol = CUT_SYMBOLS[conditionType];
  if (!symbol) return null;
  if (String(authoredThreshold ?? '').trim() === '') return null;
  if (!Number.isFinite(resolved)) return null;

  const below = conditionType === 'lt' || conditionType === 'lte';
  return [{
    kind: 'chip',
    label: `${symbol} ${formatValue(resolved)}`,
    evaluation,
    // The half-line the cut actually covers, so the legend lights it for a submission that landed
    // in it — the same identity an interval chip carries.
    sourceId: intervalSourceId(scope, below ? -Infinity : resolved, below ? resolved : Infinity),
  }];
}

/** Chips with the same word between each pair — the shape every surface draws for a fan-out. */
export function joinChips(chips: ConditionChip[], word: 'and' | 'or'): ConditionChips {
  return chips.flatMap((c, i) => (i === 0 ? [c] : [{ kind: 'connector' as const, word }, c]));
}

/**
 * A chip list as plain text, for a surface that cannot draw boxes.
 *
 * Flow renders labels as text, so this is how Flow reads the same list the chips are drawn from —
 * which is the point of the whole exercise: Flow's string becomes a RENDERING of the one derivation
 * rather than a second derivation that happens to agree today.
 *
 * `label` is a `ReactNode`, so it may be a string, a number, or an element (MCQ puts a media icon
 * in front of its option text). Only the string-ish cases can be flattened honestly; an element is
 * skipped rather than stringified, because `[object Object]` in a Flow card is worse than a chip
 * the card leaves out. Callers that need element content in Flow must pass a text fallback.
 */
/**
 * The same chips as a LIST — "a, b, c" — with the connectors dropped.
 *
 * The second rendering of one derivation, for the ANSWER BAR. That surface enumerates what will be
 * accepted; the feedback panel and the test legend state a CONDITION. Those want different words
 * for the same fact: a dual-handle slider's Correct condition genuinely reads "Slider 1: 55 to 65
 * AND Slider 2: 70 to 80", because both must hold — but its answer bar is a flowing line of
 * accepted values, and every other block's answer bar comma-joins them (numerical's editor bar and
 * slider's own collapsed card bar both do, the latter with a comment saying it matches numerical).
 *
 * Slider's editor bar was the one place that flattened the condition wording into the list slot, so
 * one block printed "x and x" in the editor and "x, x" on the card for the same answer.
 *
 * A list, not a sentence, so the connectors are DROPPED rather than translated: turning "and" into
 * a comma is what a list separator already means, and keeping both would read "x, and, y".
 */
export function chipsToListText(
  chips: ConditionChips | null,
  words: { unreachable: string },
): string | null {
  if (!chips) return null;
  const parts = chips.flatMap(c => {
    if (c.kind === 'connector') return [];
    if (c.kind === 'unreachable') return [c.label ?? words.unreachable];
    if (c.text !== undefined) return c.text.trim() ? [c.text] : [];
    if (typeof c.label === 'string') return c.label.trim() ? [c.label] : [];
    if (typeof c.label === 'number') return [String(c.label)];
    return [];
  });
  return parts.length ? parts.join(', ') : null;
}

export function chipsToPlainText(
  chips: ConditionChips | null,
  words: { and: string; or: string; unreachable: string },
): string | null {
  if (!chips || chips.length === 0) return null;
  const parts: string[] = [];
  for (const c of chips) {
    if (c.kind === 'connector') { parts.push(words[c.word]); continue; }
    // The chip's own wording wins, exactly as it does in both renderers. Without this a flattening
    // surface turned "Blank 2: Unreachable" back into a bare "Unreachable", which is the reading
    // the label exists to prevent: it says the whole state is dead rather than naming the one term
    // that is.
    if (c.kind === 'unreachable') { parts.push(c.label ?? words.unreachable); continue; }
    // The explicit fallback first: a block that supplies one has said what its element means, and
    // that beats anything guessed from the node.
    if (c.text !== undefined) { if (c.text.trim()) parts.push(c.text); continue; }
    const l = c.label;
    if (typeof l === 'string') { if (l.trim()) parts.push(l); continue; }
    if (typeof l === 'number') { parts.push(String(l)); continue; }
    // An element with no `text` carries nothing reliable — see the field's own note.
  }
  return parts.length ? parts.join(' ') : null;
}

// ─── Which interval a submission landed in ───────────────────────────────────

/**
 * The `sourceId` for a chip that covers a numeric interval, so the legend can light ONLY the one a
 * submission fell in.
 *
 * `toTestBarChips` applies `active` state-wide to every chip that carries no `sourceId`, which is
 * right for a condition written across several boxes and wrong for a fan-out over ALTERNATIVES. The
 * option-based blocks got their identity for free (an option id); the value-range blocks emit bands
 * and gaps, which have no id of their own, so every chip of a state lit together — a slider at 6.9
 * lit "< 26.88" and "> 29.12" and both of slider 2's gaps at once, which tells a teacher nothing
 * about where their handle actually is.
 *
 * The interval IS the identity, so it is encoded here rather than invented alongside. `scope`
 * separates parallel number lines that share a chip list — slider's two handles — and is empty for
 * blocks with one.
 */
export function intervalSourceId(scope: string, lo: number, hi: number): string {
  const n = (v: number) => v === -Infinity ? '-inf' : v === Infinity ? 'inf' : String(v);
  return `iv|${scope}|${n(lo)}|${n(hi)}`;
}

/**
 * The ids of the interval chips a submission landed in — at most one per scope.
 *
 * Reads the chips' OWN encoded intervals rather than recomputing them, which is what keeps this
 * from becoming a second derivation of the same bands: the caller already holds the list.
 *
 * At most one per scope, and the FIRST match wins: adjacent gaps share a boundary, so a value
 * sitting exactly on it is inside both by inclusive containment, and lighting two chips for one
 * reading would restate the problem this exists to fix.
 */
export function activeIntervalIds(
  chips: ConditionChips, valueByScope: Record<string, number | null>,
): ReadonlySet<string> {
  const out = new Set<string>();
  const claimed = new Set<string>();
  for (const c of chips) {
    if (c.kind !== 'chip' || !c.sourceId?.startsWith('iv|')) continue;
    const [, scope, loRaw, hiRaw] = c.sourceId.split('|');
    if (scope === undefined || claimed.has(scope)) continue;
    const v = valueByScope[scope];
    if (v === null || v === undefined || !Number.isFinite(v)) continue;
    const lo = loRaw === '-inf' ? -Infinity : Number(loRaw);
    const hi = hiRaw === 'inf' ? Infinity : Number(hiRaw);
    if (v >= lo && v <= hi) { out.add(c.sourceId); claimed.add(scope); }
  }
  return out;
}

/**
 * Whether the answer bar has anything to say — the predicate its SHELL must gate on.
 *
 * `AnswerBarText` returns null for an empty reading, but the tinted `CollapsedBlockBar` wrapping it
 * is the caller's, and a caller that gated on `chips.length > 0` rendered the shell around nothing:
 * an unreachable list is one entry long, so a fresh block drew an empty coloured bar. The two
 * conditions have to be asked together, which is what this is for.
 */
export function answerBarHasContent(chips: ConditionChips, authored: boolean): boolean {
  return chips.some(c => c.kind === 'chip') || authored;
}
